import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { startLoadingFlowEngineCatalogs } from "../../app/actions/flowEngineActions";
import { startLoadingCatalogsAll } from "../../app/actions/catalogActions";
import { listFlowRoleUsers, listUsersByRoles } from "../../app/actions/userSearchActions";
import { FLOW_MODULE_DEFINITIONS } from "../../app/helpers/flowEngineConfigHelper";
import { CMI_SPECIALIST_USER_ROLE_KEYS, formatSpecialistUserLabel } from "../../app/helpers/flowCaseMassHelper";
import { getCasePhaseMeta, resolveCasePhase } from "../../app/helpers/flowCasePhaseHelper";
import FlowStateSemaphore from "./components/FlowStateSemaphore";
import FlowRecordHistoryModal from "./components/FlowRecordHistoryModal";
import SpecialistTableRowActions from "./SpecialistTableRowActions";
import { FlowListEmptyRow } from "./FlowListPhaseCells";
import TrayVisionName from "./TrayVisionName";

const resolveRecordName = (record = {}) =>
  record.name || record.vision || record.purpose || "";

const resolveConcernLabel = (row, listConcerns = []) => {
  const concernId = row?.catalogconcern_id;
  if (concernId === undefined || concernId === null || concernId === "") {
    return "—";
  }
  const found = listConcerns.find((item) => Number(item.id) === Number(concernId));
  return found?.name || found?.description || "—";
};

const mergeUsersById = (...lists) => {
  const byId = new Map();
  lists.flat().forEach((user) => {
    if (user?.id == null) {
      return;
    }
    const key = Number(user.id);
    if (!byId.has(key)) {
      byId.set(key, user);
    }
  });
  return [...byId.values()];
};

const FlowModuleListTrayTable = ({
  moduleProcess,
  data = [],
  onEditRow,
  onDeleteRow,
  flowSelectionEnabled = false,
  selectedIds = [],
  onToggleRow,
  onToggleAll,
  onExcelApplied,
  canSelectRecord = null,
  editTipKey,
  editTipFallback,
  deleteTipKey,
  deleteTipFallback
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const flowEngine = useSelector((state) => state.flowEngine);
  const listConcerns = useSelector((state) => state.catalog?.listConcerns || []);
  const [users, setUsers] = useState([]);
  const [historyRecord, setHistoryRecord] = useState(null);
  const flowStates = flowEngine.states || [];
  const rows = Array.isArray(data) ? data : [];
  const isVision = moduleProcess === "vision";
  const operableRows = rows.filter((row) => !canSelectRecord || canSelectRecord(row));
  const allPageSelected = Boolean(
    operableRows.length
    && operableRows.every((row) => selectedIds.includes(row.id))
  );
  const colSpan = (flowSelectionEnabled ? 1 : 0) + 8 + (isVision ? 2 : 0);

  useEffect(() => {
    if (flowSelectionEnabled && !flowEngine.loaded && !flowEngine.loading) {
      dispatch(startLoadingFlowEngineCatalogs());
    }
  }, [dispatch, flowSelectionEnabled, flowEngine.loaded, flowEngine.loading]);

  useEffect(() => {
    if (isVision && !listConcerns.length) {
      dispatch(startLoadingCatalogsAll());
    }
  }, [dispatch, isVision, listConcerns.length]);

  useEffect(() => {
    let cancelled = false;
    const moduleId = FLOW_MODULE_DEFINITIONS[moduleProcess]?.moduleId;
    Promise.all([
      listFlowRoleUsers(CMI_SPECIALIST_USER_ROLE_KEYS, moduleId),
      listUsersByRoles(CMI_SPECIALIST_USER_ROLE_KEYS)
    ]).then(([assigned, byRole]) => {
      if (!cancelled) {
        setUsers(mergeUsersById(assigned, byRole));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [moduleProcess]);

  const stateLabel = (id) =>
    flowStates.find((item) => Number(item.id) === Number(id))?.name || id || "—";

  const userLabel = (row) => {
    if (!row?.current_user_id) {
      if (row?.published_at || !row?.current_role_catalog_id) {
        return "—";
      }
      return t("flow.pool.filter.pool-badge", "Pool");
    }
    const found = users.find((user) => Number(user.id) === Number(row.current_user_id));
    return found ? formatSpecialistUserLabel(found) : `#${row.current_user_id}`;
  };

  const nameHeader = isVision
    ? t("vision.short.vision", "Visión")
    : t("flow.cmi.col.name", "Nombre");

  return (
    <div className="table-responsive mb-4" style={{ paddingRight: "25px", paddingLeft: "25px" }}>
      <table className="table table-sm table-hover">
        <thead>
          <tr>
            {flowSelectionEnabled && (
              <th style={{ width: "40px" }}>
                <input
                  type="checkbox"
                  checked={allPageSelected}
                  onChange={onToggleAll}
                  disabled={!operableRows.length}
                />
              </th>
            )}
            <th>{t("general.id", "Id")}</th>
            <th>{nameHeader}</th>
            <th>{t("general.flow-engine.table.header-code", "Código")}</th>
            {isVision && (
              <>
                <th>{t("vision.short.topic", "Tópico")}</th>
                <th>{t("vision.short.catalogconcern_id", "Inquietud")}</th>
              </>
            )}
            <th>{t("flow.cmi.col.state", "Estado")}</th>
            <th>{t("flow.cmi.col.phase", "Fase")}</th>
            <th>{t("flow.cmi.user", "Usuario")}</th>
            <th>{t("general.flow-engine.table.header-updated", "Actualizado")}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <FlowListEmptyRow colSpan={colSpan} />
          ) : (
            rows.map((row) => {
              const phase = row.case_phase || resolveCasePhase(row, { process: moduleProcess });
              const meta = getCasePhaseMeta(phase);
              return (
                <tr key={row.id}>
                  {flowSelectionEnabled && (
                    <td>
                      {(!canSelectRecord || canSelectRecord(row)) ? (
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(row.id)}
                          onChange={() => onToggleRow?.(row.id)}
                        />
                      ) : null}
                    </td>
                  )}
                  <td>{row.id}</td>
                  <td>
                    {isVision ? (
                      <TrayVisionName
                        text={resolveRecordName(row)}
                        onOpen={() => onEditRow(row)}
                      />
                    ) : (
                      resolveRecordName(row)
                    )}
                  </td>
                  <td>{row.code || "—"}</td>
                  {isVision && (
                    <>
                      <td>{row.topic || "—"}</td>
                      <td>{resolveConcernLabel(row, listConcerns)}</td>
                    </>
                  )}
                  <td>
                    <FlowStateSemaphore
                      stateCatalogId={row.state_catalog_id}
                      interactive={Boolean(flowSelectionEnabled)}
                      onClick={flowSelectionEnabled ? () => setHistoryRecord(row) : undefined}
                    />
                    <span className="ms-2 small">{stateLabel(row.state_catalog_id)}</span>
                    {row.published_at && row.is_visible === false && (
                      <span className="badge bg-secondary ms-2">
                        {t("general.flow-engine.paused-badge", "No visible")}
                      </span>
                    )}
                  </td>
                  <td>
                    <span className={`badge bg-${meta.tone}`}>
                      {t(meta.key, meta.fallback)}
                    </span>
                  </td>
                  <td>{userLabel(row)}</td>
                  <td>{row.updated_at ? new Date(row.updated_at).toLocaleString() : "—"}</td>
                  <td className="text-nowrap">
                    <SpecialistTableRowActions
                      record={row}
                      onEdit={() => onEditRow(row)}
                      onDelete={() => onDeleteRow(row)}
                      editTipKey={editTipKey}
                      editTipFallback={editTipFallback}
                      deleteTipKey={deleteTipKey}
                      deleteTipFallback={deleteTipFallback}
                      tooltipScope={`${moduleProcess}-${row.id}`}
                      moduleProcess={moduleProcess}
                      onExcelApplied={onExcelApplied}
                    />
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
      {historyRecord && (
        <FlowRecordHistoryModal
          show
          onHide={() => setHistoryRecord(null)}
          record={historyRecord}
          moduleId={FLOW_MODULE_DEFINITIONS[moduleProcess]?.moduleId}
          moduleProcess={moduleProcess}
        />
      )}
    </div>
  );
};

FlowModuleListTrayTable.propTypes = {
  moduleProcess: PropTypes.string.isRequired,
  data: PropTypes.array,
  onEditRow: PropTypes.func.isRequired,
  onDeleteRow: PropTypes.func.isRequired,
  flowSelectionEnabled: PropTypes.bool,
  selectedIds: PropTypes.array,
  onToggleRow: PropTypes.func,
  onToggleAll: PropTypes.func,
  onExcelApplied: PropTypes.func,
  canSelectRecord: PropTypes.func,
  editTipKey: PropTypes.string.isRequired,
  editTipFallback: PropTypes.string,
  deleteTipKey: PropTypes.string.isRequired,
  deleteTipFallback: PropTypes.string
};

export default FlowModuleListTrayTable;
